import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./createForm.css";
import { useAuthentication } from "../../../Hooks/useAuthentication";
import { useNavigate } from "react-router-dom";

import useForm from "../../../Hooks/useForm";
import { FormQualitySealSchema } from "../../../Models/Schemas/form-supervisores";
import { FORM_QUALITY_SEAL } from "../../../Constants/FormatoSupervisores";

import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";

import Input from "../../Elements/Input/Input";
import Button from "../../Elements/Button/Button";
import Loader from "../../Elements/Loader/Loader";
import { setErrorFormSimpleUtil } from "../../../Utilities/Forms/errors";
import Modal from "../../Elements/Modal";

const formatDate = (d = new Date()) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
};

const getFolioMultiplier = (tipo) => {
    switch ((tipo || "").toUpperCase()) {
        case "SIMPLE":
            return 1;
        case "DOBLE":
            return 2;
        case "TRIPLE":
            return 3;
        default:
            return null; // 👈 no seleccionado
    }
};

const toInt = (v) => {
    const n = parseInt(String(v ?? "").replace(/[^\d-]/g, ""), 10);
    return Number.isFinite(n) ? n : null;
};

const calcTotalFromFolios = (del, al) => {
    const a = toInt(del);
    const b = toInt(al);
    if (a === null || b === null) return "";
    if (b < a) return ""; // inválido, lo maneja Yup
    return String(b - a + 1);
};

const isFilled = (v) => {
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim().length > 0;
    return true; // numbers/booleans
};

const CreateForm = () => {
    const navigate = useNavigate();
    const showToast = useToast();
    const [successModal, setSuccessModal] = useState(false);
    const [savedInfo, setSavedInfo] = useState({ FormId: null, createdAt: null });

    /*   const { username, isAuthenticated } = useAuthentication(); */
    const { username, role, isAuthenticated } = useAuthentication();
    /* const operador = username || "Operador"; */

    const operador = username || "Operador";

    const canEditDate = role === "operador custom";

    const today = useMemo(() => formatDate(new Date()), []);

    const { fetchDataCreateForm, loading, error } = useStore();

    const { values, errors, touched, handleBlur, handleChange, isValid } = useForm(
        { ...FORM_QUALITY_SEAL, fecha: today, proceso: "", turno: "", tipoFolio: "" },
        FormQualitySealSchema
    );

    const horaInicioRef = useRef(null);
    const horaTerminoRef = useRef(null);

    const openTimePicker = (ref) => {
        if (!ref?.current) return;

        // ✅ Chrome/Edge (y algunos navegadores modernos)
        if (typeof ref.current.showPicker === "function") {
            ref.current.showPicker();
            return;
        }

        // ✅ fallback
        ref.current.focus();
    };

    // ✅ TODOS obligatorios en UI
    const requiredFields = useMemo(() => {
        const base = [
            "fecha",
            "foliosDel",
            "al",
            "maquina",
            "noOT",
            "cliente",
            "logotipo",
            "proceso",
            "turno",
            "tipoFolio",
            "horaInicio",
            "horaTermino",
            "totalEntregadas",
            "modelo",
            "longitud",
            "color",
            "mermasDefecto",
            "mermasErrorHumano",
            "mermasRecupera",
            "pruebasCalidad",
            "observaciones",
        ];

        // 👇 si es OTRO, también exigir procesoOtro
        if (values.proceso === "OTRO") base.push("procesoOtro");

        return base;
    }, [values.proceso]);

    const allFilled = useMemo(() => {
        return requiredFields.every((k) => isFilled(values?.[k]));
    }, [requiredFields, values]);

    const submit = async () => {
        try {
            const payload = {
                ...values,
                proceso:
                    values.proceso === "OTRO"
                        ? `OTRO - ${String(values.procesoOtro || "").trim()}`
                        : values.proceso,
            };

            const res = await fetchDataCreateForm(payload);

            setSavedInfo({
                FormId: res?.data?.FormId || null,
                createdAt: res?.data?.createdAt || null,
            });

            setSuccessModal(true);
        } catch (e) {
            // error lo maneja el toast
        }
    };
    const goHome = () => navigate("/");

    const handleResponse = useCallback(() => {
        if (error) {
            const message =
                error instanceof Error ? error.message : error?.error || "Error desconocido";
            showToast("error", message);
        }
    }, [error, showToast]);

    useEffect(() => {
        handleResponse();
    }, [handleResponse]);

    useEffect(() => {
        const base = calcTotalFromFolios(values.foliosDel, values.al);

        const mult = getFolioMultiplier(values.tipoFolio);
        if (!base || !mult) {
            handleChange("totalEntregadas")({ target: { value: "" } });
            return;
        }

        handleChange("totalEntregadas")({
            target: { value: String(Number(base) * mult) },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.foliosDel, values.al, values.tipoFolio]);

    if (!isAuthenticated) {
        return (
            <div className="qs-wrap">
                <div className="container py-4">
                    <div className="alert alert-warning mb-0">No has iniciado sesión.</div>
                </div>
            </div>
        );
    }

    if (loading) return <Loader />;

    return (
        <>
            <Modal
                display={successModal}
                onClose={setSuccessModal}
                title="✅ Creado con éxito"
                size="medium"
                onCloseCallback={goHome}   // al cerrar, manda a /
            >
                <div className="d-flex flex-column gap-2">
                    <div className="alert alert-success mb-0">
                        El formulario se guardó correctamente.
                    </div>

                    {(savedInfo.FormId || savedInfo.createdAt) && (
                        <div className="small text-muted">
                            {savedInfo.FormId && (
                                <div>
                                    <span className="fw-bold">FormId:</span> {savedInfo.FormId}
                                </div>
                            )}
                            {savedInfo.createdAt && (
                                <div>
                                    <span className="fw-bold">Fecha:</span> {savedInfo.createdAt}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <button className="btn btn-success" onClick={goHome}>
                            Ir al inicio
                        </button>
                    </div>
                </div>
            </Modal>
            <div className="qs-wrap">
                <div className="container py-4">
                    <div className="card qs-card shadow-sm">
                        <div className="card-body p-3 p-md-4">
                            {/* Header */}
                            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 border-bottom pb-3 mb-3">
                                <div>
                                    <div className="qs-title m-0">Quality Seal</div>
                                    <div className="qs-sub text-muted">El protector de su Negocio</div>
                                </div>
                                <div className="small text-muted">
                                    Operador: <span className="fw-bold text-dark">{operador}</span>
                                </div>
                            </div>

                            {/* Datos generales */}
                            <div className="row g-3">
                                <div className="col-12 col-lg-8">
                                    <div className="card qs-innercard">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-3">Datos generales</div>

                                            <div className="row g-3">
                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        type={canEditDate ? "date" : "text"}
                                                        label="FECHA"
                                                        value={values.fecha}
                                                        onChange={handleChange("fecha")}
                                                        onBlur={handleBlur("fecha")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "fecha")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "fecha")}
                                                        disabled={!canEditDate}
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        label="MAQUINA"
                                                        value={values.maquina}
                                                        onChange={handleChange("maquina")}
                                                        onBlur={handleBlur("maquina")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "maquina")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "maquina")}
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        label="FOLIOS DEL"
                                                        value={values.foliosDel}
                                                        onChange={handleChange("foliosDel")}
                                                        onBlur={handleBlur("foliosDel")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "foliosDel")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "foliosDel")}
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        label="AL"
                                                        value={values.al}
                                                        onChange={handleChange("al")}
                                                        onBlur={handleBlur("al")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "al")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "al")}
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        label="No. O.T."
                                                        value={values.noOT}
                                                        onChange={handleChange("noOT")}
                                                        onBlur={handleBlur("noOT")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "noOT")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "noOT")}
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        label="CLIENTE"
                                                        value={values.cliente}
                                                        onChange={handleChange("cliente")}
                                                        onBlur={handleBlur("cliente")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "cliente")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "cliente")}
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <Input
                                                        label="LOGOTIPO"
                                                        value={values.logotipo}
                                                        onChange={handleChange("logotipo")}
                                                        onBlur={handleBlur("logotipo")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "logotipo")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "logotipo")}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Proceso / turno */}
                                <div className="col-12 col-lg-4">
                                    <div className="card qs-innercard mb-3">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-2">Proceso</div>

                                            <div className="d-flex flex-wrap gap-2">

                                                {/* Placeholder */}
                                                <label className={`btn btn-sm ${!values.proceso ? "btn-danger" : "btn-outline-secondary"}`}>
                                                    <input
                                                        className="d-none"
                                                        type="radio"
                                                        name="proceso"
                                                        value=""
                                                        checked={!values.proceso}
                                                        onChange={(e) => handleChange("proceso")(e)}
                                                    />
                                                    Selecciona…
                                                </label>

                                                {[
                                                    "ULTRASONIDO",
                                                    "PLASMA",
                                                    "IMPRESION",
                                                    "Hot stamping",
                                                    "Transfer",
                                                    "Laser",
                                                    "Ink Jet",
                                                ].map((p) => (
                                                    <label
                                                        key={p}
                                                        className={`btn btn-sm ${values.proceso === p ? "btn-primary" : "btn-outline-primary"
                                                            }`}
                                                    >
                                                        <input
                                                            className="d-none"
                                                            type="radio"
                                                            name="proceso"
                                                            value={p}
                                                            checked={values.proceso === p}
                                                            onChange={(e) => handleChange("proceso")(e)}
                                                        />
                                                        {p}
                                                    </label>
                                                ))}

                                                {/* 🔥 NUEVA OPCIÓN OTRO */}
                                                <label
                                                    className={`btn btn-sm ${values.proceso === "OTRO" ? "btn-dark" : "btn-outline-dark"
                                                        }`}
                                                >
                                                    <input
                                                        className="d-none"
                                                        type="radio"
                                                        name="proceso"
                                                        value="OTRO"
                                                        checked={values.proceso === "OTRO"}
                                                        onChange={(e) => handleChange("proceso")(e)}
                                                    />
                                                    OTRO
                                                </label>
                                            </div>

                                            {values.proceso === "OTRO" && (
                                                <div className="mt-3">
                                                    <Input
                                                        label="Especifica el proceso"
                                                        value={values.procesoOtro}
                                                        onChange={handleChange("procesoOtro")}
                                                        onBlur={handleBlur("procesoOtro")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "procesoOtro")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "procesoOtro")}
                                                    />
                                                </div>
                                            )}

                                            <div className="mt-2 small text-danger">
                                                {setErrorFormSimpleUtil(errors, touched, "proceso")}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card qs-innercard mt-3">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-2">Tipo de folio</div>

                                            <div className="d-flex flex-wrap gap-2">
                                                {/* placeholder */}
                                                <label className={`btn btn-sm ${!values.tipoFolio ? "btn-danger" : "btn-outline-secondary"}`}>
                                                    <input
                                                        className="d-none"
                                                        type="radio"
                                                        name="tipoFolio"
                                                        value=""
                                                        checked={!values.tipoFolio}
                                                        onChange={(e) => handleChange("tipoFolio")(e)}
                                                    />
                                                    Selecciona…
                                                </label>

                                                {["SIMPLE", "DOBLE", "TRIPLE"].map((t) => (
                                                    <label
                                                        key={t}
                                                        className={`btn btn-sm ${values.tipoFolio === t ? "btn-warning" : "btn-outline-warning"}`}
                                                    >
                                                        <input
                                                            className="d-none"
                                                            type="radio"
                                                            name="tipoFolio"
                                                            value={t}
                                                            checked={values.tipoFolio === t}
                                                            onChange={(e) => handleChange("tipoFolio")(e)}
                                                        />
                                                        {t}
                                                    </label>
                                                ))}
                                            </div>

                                            <div className="mt-2 small text-danger">
                                                {setErrorFormSimpleUtil(errors, touched, "tipoFolio")}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card qs-innercard">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-2">Turno</div>

                                            <div className="d-flex flex-wrap gap-2">
                                                {/* ✅ placeholder */}
                                                <label className={`btn btn-sm ${!values.turno ? "btn-danger" : "btn-outline-secondary"}`}>
                                                    <input
                                                        className="d-none"
                                                        type="radio"
                                                        name="turno"
                                                        value=""
                                                        checked={!values.turno}
                                                        onChange={(e) => handleChange("turno")(e)}
                                                    />
                                                    Selecciona…
                                                </label>

                                                {["1er", "2do"].map((t) => (
                                                    <label
                                                        key={t}
                                                        className={`btn btn-sm ${values.turno === t ? "btn-success" : "btn-outline-success"
                                                            }`}
                                                    >
                                                        <input
                                                            className="d-none"
                                                            type="radio"
                                                            name="turno"
                                                            value={t}
                                                            checked={values.turno === t}
                                                            onChange={(e) => handleChange("turno")(e)}
                                                        />
                                                        {t}
                                                    </label>
                                                ))}
                                            </div>

                                            <div className="mt-2 small text-danger">
                                                {setErrorFormSimpleUtil(errors, touched, "turno")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Producto / Producción */}
                            <div className="row g-3 mt-0 mt-md-1">
                                <div className="col-12 col-lg-4">
                                    <div className="card qs-innercard h-100">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-3">Producto</div>

                                            <Input
                                                label="MODELO"
                                                value={values.modelo}
                                                onChange={handleChange("modelo")}
                                                onBlur={handleBlur("modelo")}
                                                error={setErrorFormSimpleUtil(errors, touched, "modelo")}
                                                success={!setErrorFormSimpleUtil(errors, touched, "modelo")}
                                            />

                                            <div className="mt-3">
                                                <Input
                                                    label="LONGITUD"
                                                    value={values.longitud}
                                                    onChange={handleChange("longitud")}
                                                    onBlur={handleBlur("longitud")}
                                                    error={setErrorFormSimpleUtil(errors, touched, "longitud")}
                                                    success={!setErrorFormSimpleUtil(errors, touched, "longitud")}
                                                />
                                            </div>

                                            <div className="mt-3">
                                                <Input
                                                    label="COLOR"
                                                    value={values.color}
                                                    onChange={handleChange("color")}
                                                    onBlur={handleBlur("color")}
                                                    error={setErrorFormSimpleUtil(errors, touched, "color")}
                                                    success={!setErrorFormSimpleUtil(errors, touched, "color")}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-lg-8">
                                    <div className="card qs-innercard h-100">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-3">Producción</div>

                                            <div className="row g-3">
                                                <div
                                                    className="col-12 col-md-6"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => openTimePicker(horaInicioRef)}
                                                >
                                                    <Input
                                                        inputRef={horaInicioRef}
                                                        type="time"
                                                        step="60"
                                                        label="HORA DE INICIO"
                                                        value={values.horaInicio}
                                                        onChange={handleChange("horaInicio")}
                                                        onBlur={handleBlur("horaInicio")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "horaInicio")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "horaInicio")}
                                                    />
                                                </div>

                                                <div
                                                    className="col-12 col-md-6"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => openTimePicker(horaTerminoRef)}
                                                >
                                                    <Input
                                                        inputRef={horaTerminoRef}
                                                        type="time"
                                                        step="60"
                                                        label="HORA DE TÉRMINO"
                                                        value={values.horaTermino}
                                                        onChange={handleChange("horaTermino")}
                                                        onBlur={handleBlur("horaTermino")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "horaTermino")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "horaTermino")}
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <Input
                                                        label="TOTAL DE PIEZAS ENTREGADAS"
                                                        value={values.totalEntregadas}
                                                        onChange={handleChange("totalEntregadas")}
                                                        onBlur={handleBlur("totalEntregadas")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "totalEntregadas")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "totalEntregadas")}
                                                        disabled
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label fw-bold">
                                                        OBSERVACIONES DEL TURNO / INCIDENCIA
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        name="observaciones"
                                                        value={values.observaciones}
                                                        onChange={(e) => handleChange("observaciones")(e)}
                                                        onBlur={(e) => handleBlur("observaciones")(e)}
                                                        rows={4}
                                                    />
                                                    <div className="small text-danger mt-1">
                                                        {setErrorFormSimpleUtil(errors, touched, "observaciones")}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Merma + Firmas */}
                            <div className="row g-3 mt-0 mt-md-1">
                                <div className="col-12 col-lg-8">
                                    <div className="card qs-innercard">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-3">Sección de merma</div>

                                            <div className="row g-3">
                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        label="TOTAL DE PIEZAS POR DEFECTO"
                                                        value={values.mermasDefecto}
                                                        onChange={handleChange("mermasDefecto")}
                                                        onBlur={handleBlur("mermasDefecto")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "mermasDefecto")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "mermasDefecto")}
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <Input
                                                        label="TOTAL DE PIEZAS POR ERROR HUMANO"
                                                        value={values.mermasErrorHumano}
                                                        onChange={handleChange("mermasErrorHumano")}
                                                        onBlur={handleBlur("mermasErrorHumano")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "mermasErrorHumano")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "mermasErrorHumano")}
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <Input
                                                        label="TOTAL POR ERROR HUMANO QUE SE PUEDE RECUPERAR"
                                                        value={values.mermasRecupera}
                                                        onChange={handleChange("mermasRecupera")}
                                                        onBlur={handleBlur("mermasRecupera")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "mermasRecupera")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "mermasRecupera")}
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <Input
                                                        label="PRUEBAS DE CALIDAD EN PIEZAS"
                                                        value={values.pruebasCalidad}
                                                        onChange={handleChange("pruebasCalidad")}
                                                        onBlur={handleBlur("pruebasCalidad")}
                                                        error={setErrorFormSimpleUtil(errors, touched, "pruebasCalidad")}
                                                        success={!setErrorFormSimpleUtil(errors, touched, "pruebasCalidad")}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-lg-4">
                                    <div className="card qs-innercard mb-3">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-2">Firma operador</div>
                                            <div className="qs-signvalue">{operador}</div>
                                        </div>
                                    </div>

                                    <div className="card qs-innercard">
                                        <div className="card-body">
                                            <div className="qs-section-title mb-2">Firma supervisor</div>
                                            <div className="text-muted">(pendiente)</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="d-flex justify-content-end mt-3 gap-2">
                                <Button
                                    text="Guardar"
                                    color="green"
                                    fill
                                    action={submit}
                                    disabled={!isValid || !allFilled}
                                />
                            </div>

                            {!allFilled && (
                                <div className="alert alert-info mt-3 mb-0">
                                    Completa todos los campos para poder guardar.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </>

    );
};

export default CreateForm;